import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { formatFCFA } from "@/lib/format";
import { Loader } from "@/components/common";
import Modal, { TextField } from "@/components/Modal";
import { PageTitle, Panel } from "./AdminShell";
import { Plus, MapPin, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminZones() {
  const [zones, setZones] = useState(null);
  const [form, setForm] = useState(null); // null | {} (new) | zone (edit)
  const load = useCallback(() => api.get("/zones").then((r) => setZones(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!zones) return <Loader />;

  const remove = async (z) => {
    if (!window.confirm(`Supprimer la zone « ${z.name} » ?`)) return;
    try { await api.delete(`/zones/${z.id}`); toast.success("Zone supprimée"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div>
      <PageTitle action={<button onClick={() => setForm({})} className="bg-[#C85A32] text-white font-bold text-sm px-4 py-2 rounded-lg flex items-center gap-1" data-testid="add-zone-btn"><Plus className="w-4 h-4" /> Nouvelle zone</button>}>Zones géographiques</PageTitle>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {zones.map((z) => (
          <Panel key={z.id} className="p-4 flex items-center gap-3" data-testid={`zone-card-${z.id}`}>
            <div className="w-10 h-10 rounded-lg bg-[#FDF3EE] flex items-center justify-center shrink-0"><MapPin className="w-5 h-5 text-[#C85A32]" /></div>
            <div className="min-w-0"><p className="font-semibold truncate">{z.name}</p><p className="text-xs text-slate-400">{z.city}{z.commune ? ` · ${z.commune}` : ""}</p></div>
            <span className="ml-auto font-num font-bold text-sm text-[#C85A32] shrink-0">{formatFCFA(z.delivery_fee)}</span>
            <div className="flex gap-1 shrink-0">
              <button onClick={() => setForm(z)} className="p-1.5 text-slate-400 hover:text-[#0F172A]" data-testid={`zone-edit-${z.id}`}><Pencil className="w-4 h-4" /></button>
              <button onClick={() => remove(z)} className="p-1.5 text-slate-300 hover:text-red-500" data-testid={`zone-del-${z.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          </Panel>
        ))}
      </div>
      {form && <ZoneForm zone={form.id ? form : null} onClose={() => setForm(null)} onDone={load} />}
    </div>
  );
}

function ZoneForm({ zone, onClose, onDone }) {
  const isEdit = !!zone;
  const [f, setF] = useState({ name: zone?.name || "", city: zone?.city || "Lomé", commune: zone?.commune || "", quartier: zone?.quartier || "", delivery_fee: zone?.delivery_fee ?? 2000 });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...f, delivery_fee: Number(f.delivery_fee) };
    try {
      if (isEdit) { await api.put(`/zones/${zone.id}`, payload); toast.success("Zone mise à jour"); }
      else { await api.post("/zones", payload); toast.success("Zone ajoutée"); }
      onDone(); onClose();
    } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Modal title={isEdit ? `Modifier — ${zone.name}` : "Nouvelle zone"} onClose={onClose} testid="zone-form">
      <form onSubmit={submit} className="space-y-3">
        <TextField label="Nom de la zone" value={f.name} onChange={set("name")} required testid="zf-name" />
        <div className="grid grid-cols-2 gap-3">
          <TextField label="Ville" value={f.city} onChange={set("city")} testid="zf-city" />
          <TextField label="Commune" value={f.commune} onChange={set("commune")} testid="zf-commune" required={false} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <TextField label="Quartier" value={f.quartier} onChange={set("quartier")} testid="zf-quartier" required={false} />
          <TextField label="Frais de livraison (FCFA)" type="number" value={f.delivery_fee} onChange={set("delivery_fee")} testid="zf-fee" />
        </div>
        <button className="w-full bg-[#C85A32] text-white font-bold py-3 rounded-lg" data-testid="zf-submit">Enregistrer</button>
      </form>
    </Modal>
  );
}
